import React from 'react';
import { BsX, BsPlusCircle } from 'react-icons/bs';
import { BiPencil } from 'react-icons/bi';

interface HelpDrawerUsuariosProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
}

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div style={{ marginBottom: '20px' }}>
    <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#333', marginBottom: '8px', borderBottom: '1px solid #eee', paddingBottom: '6px' }}>
      {title}
    </h4>
    <div style={{ fontSize: '13px', color: '#555', lineHeight: '1.6' }}>{children}</div>
  </div>
);

const ActionItem: React.FC<{ icon: React.ReactNode; label: string; description: string; bgColor: string; borderColor?: string; textColor?: string }> = ({ icon, label, description, bgColor, borderColor, textColor }) => (
  <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', marginBottom: '10px' }}>
    <span style={{
      fontSize: '12px',
      flexShrink: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '28px',
      height: '28px',
      borderRadius: '3px',
      border: `1px solid ${borderColor || '#ccc'}`,
      backgroundColor: bgColor,
      color: textColor || 'black',
    }}>{icon}</span>
    <div>
      <strong>{label}</strong>
      <div style={{ color: '#666', fontSize: '12px' }}>{description}</div>
    </div>
  </div>
);

const DRAWER_WIDTH = 320;

const HelpDrawerUsuarios: React.FC<HelpDrawerUsuariosProps> = ({ isOpen, onClose, children }) => {
  return (
    <>
      <div style={{ marginRight: isOpen ? DRAWER_WIDTH : 0, transition: 'margin-right 0.3s ease' }}>
        {children}
      </div>

      <div style={{
        position: 'fixed',
        top: 0,
        right: isOpen ? 0 : -DRAWER_WIDTH,
        width: DRAWER_WIDTH,
        height: '100vh',
        backgroundColor: '#fff',
        borderLeft: '1px solid #e0e0e0',
        boxShadow: '-2px 0 8px rgba(0,0,0,0.08)',
        transition: 'right 0.3s ease',
        zIndex: 1050,
        display: 'flex',
        flexDirection: 'column',
      }}>
        <div style={{
          padding: '12px 16px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          background: 'linear-gradient(180deg, #2a2d3a 0%, #3c3f4c 100%)',
          color: '#fff',
        }}>
          <h3 style={{ margin: 0, fontSize: '14px', fontWeight: '600' }}>Ajuda — Gerenciar Usuários</h3>
          <button
            onClick={onClose}
            style={{ background: 'transparent', border: 'none', color: '#fff', fontSize: '18px', cursor: 'pointer', padding: '0 4px' }}
          >
            <BsX />
          </button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '16px', fontSize: '13px' }}>
          <Section title="Visão Geral">
            <p style={{ margin: 0 }}>
              Gerencie os usuários credenciados no sistema. Visualize, filtre e altere o perfil de acesso de cada usuário.
            </p>
          </Section>

          <Section title="Filtros">
            <ul style={{ margin: 0, paddingLeft: '18px' }}>
              <li><strong>Nome:</strong> Busca pelo nome do usuário.</li>
              <li><strong>Email:</strong> Busca pelo email.</li>
              <li><strong>Matrícula:</strong> Busca pela matrícula institucional.</li>
              <li><strong>Perfil:</strong> Busca pelo nome do perfil atribuído.</li>
            </ul>
            <p style={{ margin: '8px 0 0 0', fontSize: '12px', color: '#888' }}>
              Filtros aplicados automaticamente conforme você digita.
            </p>
          </Section>

          <Section title="Colunas da Tabela">
            <ul style={{ margin: 0, paddingLeft: '18px' }}>
              <li><strong>Nome:</strong> Nome completo do usuário.</li>
              <li><strong>Email:</strong> Email institucional.</li>
              <li><strong>Matrícula:</strong> Matrícula ou "Não informado".</li>
              <li><strong>Perfil:</strong> Perfil de acesso atual (badge colorido).</li>
              <li><strong>Ações:</strong> Editar perfil (requer permissão).</li>
            </ul>
          </Section>

          <Section title="Ações">
            <ActionItem icon={<BiPencil />} label="Editar Perfil" description="Altera o perfil de acesso do usuário. Define quais permissões ele terá no sistema." bgColor="#e3f2fd" borderColor="gray" />
            <ActionItem icon={<BsPlusCircle style={{ color: 'white' }} />} label="Criar Perfil" description="Cria um novo perfil de acesso com permissões personalizadas." bgColor="#28a745" borderColor="#28a745" textColor="white" />
          </Section>

          <Section title="Perfis do Sistema">
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '12px' }}>
              {[
                { perfil: 'ADM', desc: 'Acesso total ao sistema.' },
                { perfil: 'DEDH', desc: 'Gestão de bancas, candidatos e composições.' },
                { perfil: 'Presidente', desc: 'Votação, ATA e gestão durante a banca.' },
                { perfil: 'Técnico', desc: 'Apoio técnico durante a realização da banca.' },
                { perfil: 'Secretário', desc: 'Apoio administrativo na banca.' },
                { perfil: 'Sem perfil', desc: 'Usuário sem permissões atribuídas.' },
              ].map(({ perfil, desc }) => (
                <div key={perfil} style={{ display: 'flex', gap: '8px' }}>
                  <strong style={{ minWidth: '80px' }}>{perfil}:</strong>
                  <span>{desc}</span>
                </div>
              ))}
            </div>
          </Section>

          <Section title="Dicas">
            <ul style={{ margin: 0, paddingLeft: '18px' }}>
              <li>Usuários são criados automaticamente no primeiro login via Keycloak.</li>
              <li>A edição de perfil requer a permissão "alterar-usuario".</li>
              <li>Perfis de membros de banca (Presidente, Técnico) são atribuídos automaticamente ao definir a banca.</li>
            </ul>
          </Section>
        </div>
      </div>
    </>
  );
};

export default HelpDrawerUsuarios;
